import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore, ROBOT_IMAGES } from '../store/useStore';
import Modal from '../components/Modal';
import { toast } from '../components/Toast';
import { Star, Zap, ChevronDown } from 'lucide-react';

const templates = [
  { id: 't1', name: '霍元甲', robotModels: ['H2', 'R1'], duration: '00:45', level: '中级', desc: '经典武术风格舞蹈，刚劲有力，展现中国传统武术之美。适合庆典和表演场景。', image: ROBOT_IMAGES[0], createdAt: '2025-01-15' },
  { id: 't2', name: '街舞入门', robotModels: ['R1'], duration: '00:30', level: '初级', desc: '适合初学者的街舞动作，节奏明快，动作简洁有力。适合学习和日常娱乐。', image: ROBOT_IMAGES[1], createdAt: '2025-02-01' },
  { id: 't3', name: '机械舞', robotModels: ['H2'], duration: '01:00', level: '高级', desc: '高难度机械舞动作组合，精准控制每个关节角度。适合进阶用户和专业展示。', image: ROBOT_IMAGES[2], createdAt: '2025-02-20' },
  { id: 't4', name: '古风舞', robotModels: ['H2', 'R1'], duration: '00:50', level: '中级', desc: '融合古典元素的舞蹈，优雅柔美，展现东方韵律之美。适合文化展示和节日庆典。', image: ROBOT_IMAGES[3], createdAt: '2025-03-05' },
];

const robotFilters = ['全部', 'Unitree H2', 'Unitree R1', '智元远征A2'];

export function getTemplate(id: string) {
  return templates.find(t => t.id === id);
}

export default function OfficialTemplates() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('全部');
  const [showDeployModal, setShowDeployModal] = useState(false);  
  const [selectedTemplate, setSelectedTemplate] = useState<typeof templates[0] | null>(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  const filteredTemplates = templates.filter((t) => {
    if (activeFilter === '全部') return true;
    return t.robotModels.includes(activeFilter.replace('Unitree ', ''));
  });

  const levelStyle = (level: string) => {
    switch (level) {
      case '初级': return 'bg-success-50 text-success-dark border-success/10';
      case '中级': return 'bg-brand-50 text-brand border-brand/10';
      case '高级': return 'bg-warning-50 text-warning-dark border-warning/10';
      default: return 'bg-slate-50 text-text-secondary border-slate-200';
    }
  };

  const levelIcon = (level: string) => {
    switch (level) {
      case '高级': return <Star size={11} className="mr-0.5" />;
      case '中级': return <Zap size={11} className="mr-0.5" />;
      default: return null;
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-text-primary font-display">官方模板</h1>
          <p className="text-sm text-text-secondary mt-1.5">平台预置舞蹈模板，选择后直接部署</p>
        </div>
        <div className="relative" ref={filterRef}>
          <button onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl text-sm font-medium border border-border-subtle shadow-soft hover:bg-slate-50 transition-colors">
            <span>{activeFilter}</span>
            <ChevronDown size={14} className={`text-text-muted transition-transform duration-200 ${showFilterDropdown ? 'rotate-180' : ''}`} />
          </button>
          {showFilterDropdown && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setShowFilterDropdown(false)} />
              <div className="dropdown-enter absolute right-0 top-full mt-1.5 w-44 bg-white rounded-xl shadow-dropdown py-1 z-30">
                {robotFilters.map((f) => (
                  <button key={f} onClick={() => { setActiveFilter(f); setShowFilterDropdown(false); }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${activeFilter === f ? 'text-brand bg-brand-50 font-semibold' : 'text-text-secondary hover:bg-slate-50'}`}>
                    {f}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filteredTemplates.map((t) => (
          <div key={t.id} className="bg-white rounded-2xl shadow-soft hover-lift group">
            <div className="h-52 bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden rounded-t-2xl">
              <img src={t.image} alt={t.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              <span className={`absolute top-3 left-3 text-[11px] font-semibold px-2.5 py-1 rounded-lg border backdrop-blur-sm ${levelStyle(t.level)}`}>
                {levelIcon(t.level)}{t.level}
              </span>
            </div>
            <div className="p-5">
              <h3 className="text-[15px] font-bold text-text-primary mb-2">{t.name}</h3>
              <p className="text-xs text-text-muted mb-1 leading-relaxed line-clamp-2">{t.desc}</p>
              <div className="flex items-center gap-3 text-[11px] text-text-muted mb-4 mt-3">
                <span>适配：{t.robotModels.map(r => 'Unitree ' + r).join(' / ')}</span>
                <span>·</span>
                <span>{t.duration}</span>
              </div>
              <button onClick={(e) => { e.stopPropagation(); setSelectedTemplate(t); setShowDeployModal(true); }}
                className="w-full py-2.5 gradient-brand text-white rounded-xl text-sm font-semibold hover-lift">
                去App执行
              </button>
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={showDeployModal} onClose={() => setShowDeployModal(false)} title="执行提示">
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-brand" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
          </div>
          <p className="text-sm text-text-primary font-semibold mb-1">「{selectedTemplate?.name}」已同步至手机端</p>
          <p className="text-xs text-text-muted">请在「赛博司令」App → ODance 中连接机器人并执行</p>
        </div>
      </Modal>
    </div>
  );
}
